import React, { useState, useCallback } from 'react';
import { useImageLoader } from '../hooks/useImageLoader';

function HomePage() {
  const [currentPhotoInfo, setCurrentPhotoInfo] = useState(null);
  const [photoUrl, setPhotoUrl] = useState(null);
  
  const { 
    progress: loadProgress, 
    isLoading: loading, 
    blobUrl: currentPhoto,
    loadImage,
    reset
  } = useImageLoader(null, { autoLoad: false });

  const loadRandomPhoto = useCallback(async () => {
    reset();
    setCurrentPhotoInfo(null);
    
    try {
      // First get photo info with EXIF data
      const infoResponse = await fetch('/api/random-photo-info');
      if (infoResponse.ok) {
        const photoInfo = await infoResponse.json();
        setCurrentPhotoInfo(photoInfo);

        // The 4k display copy, not the original: this is shown at 70vh, so the
        // remaining ~40 MB of an original buys nothing you can see.
        //
        // Note there is deliberately no cache-buster on the end of this. The
        // `?t=${Date.now()}` that used to be here made every request a URL the
        // browser had never seen, which meant pressing the button twice on the
        // same photo downloaded it twice and nothing in front of the site could
        // cache any of it.
        const url = photoInfo.displayUrl || photoInfo.url;
        setPhotoUrl(url);
        loadImage(url);
      } else {
        console.error('Failed to load photo info');
        setCurrentPhotoInfo(null);
      }
    } catch (err) {
      console.error('Error loading photo:', err);
      setCurrentPhotoInfo(null);
    }
  }, [loadImage, reset]);

  const formatExifValue = (value, type) => {
    if (value === null || value === undefined) return 'N/A';
    
    switch (type) {
      case 'exposureTime':
        if (value < 1) {
          return `1/${Math.round(1/value)}s`;
        }
        return `${value}s`;
      case 'fStop':
        return `f/${value}`;
      case 'focalLength':
        return `${value}mm`;
      case 'iso':
        return `ISO ${value}`;
      case 'date':
        if (value) {
          const date = new Date(value);
          return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
        }
        return 'N/A';
      default:
        return value;
    }
  };

  return (
    <div className="page-content">
      <h1>Keaton Dalquist</h1>
      <p className="description">
        I am a software engineer who also enjoys photography.
      </p>
      
      <button 
        className="photo-button" 
        onClick={loadRandomPhoto}
        disabled={loading}
      >
        {loading ? 'Loading...' : 'Load Random Photo'}
      </button>
      
      {/* One box holds both the progress bar and the photo that replaces it, so
          the page doesn't shunt everything downwards the moment an image lands.
          It only exists once you've asked for a photo. */}
      {(loading || currentPhoto) && (
        <div className="photo-container">
          <div className="home-stage">
            {loading && (
              <div className="home-loading-container">
                <div className="home-progress-bar">
                  <div
                    className="home-progress-fill"
                    style={{ width: `${loadProgress}%` }}
                  />
                </div>
                <span className="home-progress-text">Loading {loadProgress}%</span>
              </div>
            )}
            {currentPhoto && !loading && (
              <img
                src={currentPhoto}
                alt="Random background"
                className="random-photo"
              />
            )}
          </div>
          
          {currentPhotoInfo && currentPhotoInfo.exif && (
            <div className="photo-stats">
              <div className="stats-grid">
                {currentPhotoInfo.place && (
                  <div className="stat-item place-item">
                    <span className="stat-label">Location:</span>
                    <span className="stat-value place-value">
                      {currentPhotoInfo.place}
                    </span>
                  </div>
                )}
                <div className="stat-item captured-item">
                  <span className="stat-label">Captured:</span>
                  <span className="stat-value captured-value">
                    {formatExifValue(currentPhotoInfo.exif.dateCaptured, 'date')}
                  </span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Camera:</span>
                  <span className="stat-value">
                    {currentPhotoInfo.exif.cameraMake && currentPhotoInfo.exif.cameraModel 
                      ? `${currentPhotoInfo.exif.cameraMake} ${currentPhotoInfo.exif.cameraModel}`
                      : 'N/A'}
                  </span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Aperture:</span>
                  <span className="stat-value">
                    {formatExifValue(currentPhotoInfo.exif.fStop, 'fStop')}
                  </span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Exposure:</span>
                  <span className="stat-value">
                    {formatExifValue(currentPhotoInfo.exif.exposureTime, 'exposureTime')}
                  </span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">ISO:</span>
                  <span className="stat-value">
                    {formatExifValue(currentPhotoInfo.exif.iso, 'iso')}
                  </span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Focal Length:</span>
                  <span className="stat-value">
                    {formatExifValue(currentPhotoInfo.exif.focalLength, 'focalLength')}
                  </span>
                </div>
                {currentPhotoInfo.exif.lens && (
                  <div className="stat-item lens-item">
                    <span className="stat-label">Lens:</span>
                    <span className="stat-value lens-value">{currentPhotoInfo.exif.lens}</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default HomePage;
